import React, { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { SendbirdProvider } from "@sendbird/uikit-react/SendbirdProvider";
import { GroupChannelList } from "@sendbird/uikit-react/GroupChannelList";
import { GroupChannel } from "@sendbird/uikit-react/GroupChannel";
import WithAppLayout from "../../components/WithAppLayout";
import WithQuery from "../../components/WithQuery";
import { GET_SENDBIRD_INIT_DATA } from "../../graphql/sendbird";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import ProfileImage from "../../components/common/ProfileImage";

const ChatPageInner = ({ currentUser, data, loading }) => {
  const [activeChannelUrl, setActiveChannelUrl] = useState(null);

  if (loading) {
    return <LoadingIndicator />;
  }

  const renderHeader = () => (
    <Flex alignItems={"center"} h="100%">
      <Text color="whiteAlpha.900" fontSize="lg" fontWeight="bold">Chats</Text>
    </Flex>
  );

  return (
    <Box h="100%" bg="black">
      <SendbirdProvider
        userId={currentUser?.id}
        appId={data?.sendbirdInitData?.appId}
        accessToken={data?.sendbirdInitData?.accessToken}
        theme="dark"
      >
        <Flex h="100%">
          {/* Left column: User/Group/Channel List */}
          <Box w="340px" bg="gray.900" borderRight="1px solid #222" p={0} h="100%" overflow="hidden">
            <Box h="100%" overflowY="auto">
              <GroupChannelList
                renderHeader={renderHeader}
                onChannelSelect={channel => channel?.url && setActiveChannelUrl(channel.url)}
                renderChannelPreview={({ channel }) => {
                  if (!channel) return null;
                  const otherMember = channel.members?.find(member => member.userId !== currentUser?.id);
                  const directChannel = data?.sendbirdInitData?.directChannels?.find(dc => dc.userId === otherMember?.userId);
                  const imageUrl = directChannel?.profileUrl || otherMember?.plainProfileUrl || null;
                  const channelName = directChannel?.nickname || otherMember?.nickname || channel.name;
                  return (
                    <Flex
                      key={channel.url}
                      align="center"
                      p={3}
                      bg={activeChannelUrl === channel.url ? "gray.700" : "transparent"}
                      cursor="pointer"
                      _hover={{ bg: "gray.800" }}
                      position="relative"
                    >
                      <Box position="relative">
                        <ProfileImage
                          imageUrl={imageUrl}
                          name={channelName}
                          size="36px"
                          marginRight="10px"
                        />
                        {channel.unreadMessageCount > 0 && (
                          <Box
                            position="absolute"
                            top="-2px"
                            right="10px"
                            bg="red.500"
                            color="white"
                            borderRadius="full"
                            minW="18px"
                            h="18px"
                            fontSize="xs"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontWeight="bold"
                          >
                            {channel.unreadMessageCount > 99 ? "99+" : channel.unreadMessageCount}
                          </Box>
                        )}
                      </Box>
                      <Box flex={1}>
                        <Flex align="center" justify="space-between">
                          <Text color="whiteAlpha.900" fontWeight="semibold">{channelName || "(No Name)"}</Text>
                          {channel.unreadMessageCount > 0 && (
                            <Box
                              w="8px"
                              h="8px"
                              bg="red.500"
                              borderRadius="full"
                              ml={2}
                            />
                          )}
                        </Flex>
                        <Text color="whiteAlpha.600" fontSize="sm">{channel.lastMessage?.message ? (channel.lastMessage.message.length < 50 ? channel.lastMessage.message : `${channel.lastMessage.message.slice(0, 50)}...`) : "No messages yet"}</Text>
                      </Box>
                    </Flex>
                  )
                }}
              />
            </Box>
          </Box>
          {/* Right column: Conversation */}
          <Box flex={1} bg="black" h="100%"
            sx={{
              '.sendbird-message-content__middle__message-item-body.sendbird-text-message-item-body.outgoing': {
                bg: 'blue.500',
                color: 'white',
              }
            }}
          >
            {activeChannelUrl ? (
              <GroupChannel
                channelUrl={activeChannelUrl}
                renderChannelHeader={() => { return null }}
              />
            ) : (
              <Flex h="100%" align="center" justify="center">
                <Text color="whiteAlpha.600">Select a conversation to start chatting</Text>
              </Flex>
            )}
          </Box>
        </Flex>
      </SendbirdProvider>
    </Box>
  );
};

function PageComponent({ currentUser }) {
  if (!currentUser?.role || !["Recruiter", "Client"].includes(currentUser.role)) {
    return null;
  }

  return WithQuery(ChatPageInner, GET_SENDBIRD_INIT_DATA, null, {
    currentUser,
  })();
}

export default WithAppLayout(PageComponent);
